import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const AdminUsers = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // New user form
  const [showForm, setShowForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [submitting, setSubmitting] = useState(false);

  // Delete modal
  const [deleteUser, setDeleteUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/admin/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error);
      setUsers(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const response = await fetch('/api/admin/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: newEmail,
          name: newName,
          role: newRole
        })
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.error);

      setSuccess(data.message || 'User created successfully!');
      setShowForm(false);
      setNewEmail('');
      setNewName('');
      setNewRole('user');
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;

    try {
      const response = await fetch(`/api/admin/users/${deleteUser.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.error);

      setSuccess('User deleted successfully');
      setDeleteUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.message);
      setDeleteUser(null);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      setError('');
      const response = await fetch(`/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.error);

      setSuccess(data.message);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div style={styles.loading}>Loading users...</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>👑 User Management</h1>
        <button onClick={() => setShowForm(!showForm)} style={styles.addBtn}>
          {showForm ? '✕ Cancel' : '+ Add User'}
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {/* Add User Form */}
      {showForm && (
        <form onSubmit={handleCreateUser} style={styles.form}>
          <h3 style={styles.formTitle}>Create New User</h3>
          <p style={styles.formNote}>
            A temporary password will be generated and sent to the user's email.
          </p>
          
          <div style={styles.formRow}>
            <div style={styles.formField}>
              <label style={styles.label}>Email Address *</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                style={styles.input}
                placeholder="user@example.com"
                required
              />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>Name (Optional)</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                style={styles.input}
                placeholder="John Doe"
              />
            </div>
            <div style={styles.formFieldSmall}>
              <label style={styles.label}>Role</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                style={styles.select}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          
          <button type="submit" style={styles.submitBtn} disabled={submitting}>
            {submitting ? 'Creating...' : 'Create User & Send Welcome Email'}
          </button>
        </form>
      )}

      {/* Users Table */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Role</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Created</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td style={styles.td}>{user.email}</td>
                <td style={styles.td}>{user.name || '-'}</td>
                <td style={styles.td}>
                  <span style={user.role === 'admin' ? styles.adminBadge : styles.userBadge}>
                    {user.role}
                  </span>
                </td>
                <td style={styles.td}>
                  {user.mustChangePassword ? (
                    <span style={styles.pendingBadge}>Pending</span>
                  ) : (
                    <span style={styles.activeBadge}>Active</span>
                  )}
                </td>
                <td style={styles.td}>
                  {new Date(user.createdAt).toLocaleDateString()}
                </td>
                <td style={styles.td}>
                  <div style={styles.actionBtns}>
                    <button
                      onClick={() => handleResetPassword(user.id)}
                      style={styles.resetBtn}
                      title="Reset Password"
                    >
                      🔄
                    </button>
                    <button
                      onClick={() => setDeleteUser(user)}
                      style={styles.deleteBtn}
                      title="Delete User"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteUser && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalIcon}>⚠️</div>
            <h3 style={styles.modalTitle}>Delete User?</h3>
            <p style={styles.modalText}>
              Are you sure you want to delete <strong>{deleteUser.email}</strong>?
              This action cannot be undone.
            </p>
            <div style={styles.modalActions}>
              <button onClick={() => setDeleteUser(null)} style={styles.cancelBtn}>
                Cancel
              </button>
              <button onClick={handleDeleteUser} style={styles.confirmDeleteBtn}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '1200px', margin: '0 auto', padding: '1rem 2rem' },
  loading: { textAlign: 'center', padding: '3rem', color: '#6b7280' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  title: { fontSize: '1.8rem', color: '#1e3a5f', margin: 0 },
  addBtn: { backgroundColor: '#7c3aed', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' },
  error: { backgroundColor: '#fee2e2', color: '#dc2626', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' },
  success: { backgroundColor: '#dcfce7', color: '#16a34a', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' },
  form: { backgroundColor: '#faf5ff', border: '2px solid #7c3aed', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem' },
  formTitle: { margin: '0 0 0.5rem 0', color: '#7c3aed' },
  formNote: { color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem' },
  formRow: { display: 'flex', gap: '1rem', flexWrap: 'wrap' },
  formField: { flex: '1 1 200px' },
  formFieldSmall: { flex: '0 0 120px' },
  label: { display: 'block', fontWeight: '500', color: '#374151', marginBottom: '0.3rem', fontSize: '0.9rem' },
  input: { width: '100%', padding: '0.6rem', borderRadius: '6px', border: '2px solid #e5e7eb', fontSize: '0.95rem', boxSizing: 'border-box' },
  select: { width: '100%', padding: '0.6rem', borderRadius: '6px', border: '2px solid #e5e7eb', fontSize: '0.95rem' },
  submitBtn: { marginTop: '1rem', backgroundColor: '#7c3aed', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' },
  tableContainer: { backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { backgroundColor: '#1e3a5f', color: 'white', padding: '1rem', textAlign: 'left', fontSize: '0.9rem' },
  td: { padding: '1rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.9rem' },
  adminBadge: { backgroundColor: '#7c3aed', color: 'white', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600' },
  userBadge: { backgroundColor: '#e5e7eb', color: '#374151', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' },
  pendingBadge: { backgroundColor: '#fef3c7', color: '#92400e', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' },
  activeBadge: { backgroundColor: '#dcfce7', color: '#16a34a', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' },
  actionBtns: { display: 'flex', gap: '0.5rem' },
  resetBtn: { backgroundColor: '#fbbf24', border: 'none', padding: '0.4rem 0.6rem', borderRadius: '4px', cursor: 'pointer' },
  deleteBtn: { backgroundColor: '#ef4444', border: 'none', padding: '0.4rem 0.6rem', borderRadius: '4px', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '400px', textAlign: 'center' },
  modalIcon: { fontSize: '3rem' },
  modalTitle: { fontSize: '1.3rem', color: '#1e3a5f', margin: '0.5rem 0' },
  modalText: { color: '#6b7280', marginBottom: '1.5rem' },
  modalActions: { display: 'flex', gap: '1rem', justifyContent: 'center' },
  cancelBtn: { backgroundColor: '#e5e7eb', color: '#374151', padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' },
  confirmDeleteBtn: { backgroundColor: '#ef4444', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' }
};

export default AdminUsers;
